import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, HelpCircle, ArrowRight } from "lucide-react";

export const TourGuide = ({ isOpen, steps = [], onClose }) => {
  // Prevent body scrolling when the drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Highlights and scrolls to the targeted element on the page
  const handleStepFocus = (targetSelector) => {
    const el = document.querySelector(targetSelector);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

      // Temporary glowing ring animation
      el.classList.add("ring-4", "ring-[#3F80F7]/50", "transition-all", "duration-300");
      setTimeout(() => {
        el.classList.remove("ring-4", "ring-[#3F80F7]/50");
      }, 1500);
    }
  };

  const handleStepHoverStart = (targetSelector) => {
    const el = document.querySelector(targetSelector);
    if (el) {
      el.classList.add("ring-2", "ring-[#3F80F7]/30", "transition-all", "duration-200");
    }
  };

  const handleStepHoverEnd = (targetSelector) => {
    const el = document.querySelector(targetSelector);
    if (el) {
      el.classList.remove("ring-2", "ring-[#3F80F7]/30");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[99999] flex justify-end overflow-hidden select-none">
          {/* Backdrop Click Capture (Outside Click Dismissal) */}
          <motion
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/10 backdrop-blur-[1px] z-[99998] cursor-pointer"
          />

          {/* Slide-out Drawer Panel */}
          <motion
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 220 }}
            className="relative w-[380px] sm:w-[420px] h-full bg-white border-l border-slate-100 z-[99999] shadow-[-10px_0_30px_rgba(15,23,42,0.06)] flex flex-col pointer-events-auto overflow-hidden font-plus"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <HelpCircle size={18} className="text-[#3F80F7]" />
                <div className="flex flex-col">
                  <h3 className="text-[12px] font-black uppercase tracking-[2px] text-slate-800">
                    Production Guide
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1 bg-white border border-slate-200 hover:border-slate-300"
              >
                <X size={16} />
              </button>
            </div>

            {/* Steps Checklist Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 no-scrollbar">

              {steps.map((step, idx) => (
                <div
                  key={idx}
                  onClick={() => handleStepFocus(step.target)}
                  onMouseEnter={() => handleStepHoverStart(step.target)}
                  onMouseLeave={() => handleStepHoverEnd(step.target)}
                  className="bg-white border border-slate-100 hover:border-[#3F80F7]/40 hover:bg-[#3F80F7]/[0.01] p-4 transition-all duration-200 cursor-pointer flex gap-4 group rounded-none"
                >
                  {/* Step Number Badge */}
                  <div className="h-6 w-6 shrink-0 bg-slate-50 border border-slate-200 text-slate-600 group-hover:bg-[#3F80F7]/10 group-hover:text-[#3F80F7] group-hover:border-[#3F80F7]/20 flex items-center justify-center text-[10px] font-black tracking-tighter transition-all">
                    {idx + 1}
                  </div>

                  {/* Content details */}
                  <div className="flex flex-col gap-1 flex-1">
                    <div className="flex justify-between items-center">
                      <h4 className="text-[12px] font-black text-slate-800 uppercase tracking-wide group-hover:text-[#3F80F7] transition-colors">
                        {step.title}
                      </h4>
                      <ArrowRight size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <p className="text-[11px] font-medium leading-relaxed text-slate-500 group-hover:text-slate-500 transition-colors">
                      {step.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 shrink-0 bg-slate-50/50 flex justify-end items-center">
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-[#3F80F7] hover:bg-[#2c5ebc] text-white text-[9px] font-black uppercase tracking-[1.5px] border border-[#3F80F7] transition-all"
              >
                Got It
              </button>
            </div>
          </motion>
        </div>
      )}
    </AnimatePresence>
  );
};
